import { getWorkflowStatus } from "api/applications";
import type { NewApplicationListItemMark } from "api/applications";

type StatusRoute = {
  slug: string;
  query?: Record<string, string>;
};

/**
 * Static mapping from a Stage 3 application status string to its top-level
 * STATUS sub-step (slug + Pass/Fail or sub-status tab). Used as the shallow
 * default and as the fallback when workflow lookup fails.
 *
 * Unknown / missing status falls back to formalities-check (the first sub-step).
 */
function resolveStatusRouteShallow(status: string | null | undefined): StatusRoute {
  const value = (status ?? "").trim();
  const lower = value.toLowerCase();
  if (!lower) return { slug: "formalities-check" };

  if (lower === "formalities check pass") {
    return { slug: "formalities-check", query: { status: "pass" } };
  }
  if (lower === "formalities check fail") {
    return { slug: "formalities-check", query: { status: "fail" } };
  }
  if (lower.includes("formalities check")) {
    return { slug: "formalities-check" };
  }

  if (lower === "marked for exam") return { slug: "marked-for-exam" };

  if (lower === "accepted") {
    return { slug: "examination-report", query: { sub: "accepted" } };
  }
  if (lower === "objected") {
    return { slug: "examination-report", query: { sub: "objected" } };
  }
  if (lower === "examination report - accepted after objection") {
    return {
      slug: "examination-report",
      query: { sub: "accepted-after-objection" },
    };
  }
  if (lower === "examination report - hearing") {
    return {
      slug: "examination-report",
      query: { sub: "hearing-after-objection" },
    };
  }
  if (lower.includes("examination report")) {
    return { slug: "examination-report" };
  }

  if (
    lower === "advertised in journal - opposed" ||
    lower === "opposition hearing"
  ) {
    return {
      slug: "advertised-in-journal",
      query: { variant: "opposed" },
    };
  }
  if (
    lower === "advertised" ||
    lower === "advertised in journal - not opposed" ||
    lower.includes("advertised in journal")
  ) {
    return {
      slug: "advertised-in-journal",
      query: { variant: "standard" },
    };
  }

  if (lower === "registered") {
    return { slug: "final-order", query: { sub: "registered" } };
  }
  if (lower === "abandoned" || lower === "refused") {
    return { slug: "final-order", query: { sub: "refused-or-abandoned" } };
  }

  // "TM No. Allotted" and any unknown default to the first sub-step.
  return { slug: "formalities-check" };
}

/**
 * Workflow step slug → FormalitiesCheckView Fail-branch URL substep id.
 * Multiple workflow steps in the Invoice for FC Failure series collapse to the
 * same UI substep ("invoice-for-fc-failure") because the view groups them.
 */
const FC_FAIL_STEP_TO_SUBSTEP: Record<string, string> = {
  S3_01B_I_STATUS_OF_FC_FAILURE: "status-of-fc-failure",
  S3_01B_II_NOTICE_TO_CLIENT_FC_FAIL: "notice-to-client",
  S3_01B_III_INFORM_CLIENT_FC_FAIL: "inform-client",
  S3_01B_IV_A_SAVE_INVOICE_FC_REPLY: "invoice-for-fc-failure",
  S3_01B_IV_B_GENERATE_INVOICE_PDF_FC_REPLY: "invoice-for-fc-failure",
  S3_01B_IV_C_SEND_INVOICE_TO_CLIENT_FC_FAIL: "invoice-for-fc-failure",
  S3_01B_IV_D_FC_REPLY_INVOICE_PAYMENT_MESSAGE: "invoice-for-fc-failure",
  S3_01B_V_CALL_CLIENT_FC_FAIL: "call-client",
  S3_01B_VI_CONFIRMATION_FROM_CLIENT_FC_FAIL: "client-confirmation",
  S3_01B_VII_DOCUMENTS_FROM_CLIENT_FC_FAIL: "documents",
  S3_01B_VIII_FILING_OF_REPLY_FC_FAIL: "filing-of-reply",
  S3_01B_IX_INFORM_CLIENT_REPLY_TO_FC_FAIL: "inform-client-done",
};

type WorkflowStep = {
  slug: string;
  isCompleted: boolean;
  isSkipped: boolean;
};

/**
 * Pick the substep for FC Fail by walking workflow steps in order and returning
 * the substep matching the latest completed / skipped step in the Fail branch.
 * Returns null when no FC Fail step has been touched yet (caller uses default).
 */
function pickFcFailSubstep(steps: WorkflowStep[]): string | null {
  let latest: string | null = null;
  for (const step of steps) {
    if (!step.slug?.startsWith("S3_01B_")) continue;
    if (!(step.isCompleted || step.isSkipped)) continue;
    const mapped = FC_FAIL_STEP_TO_SUBSTEP[step.slug];
    if (mapped) latest = mapped;
  }
  return latest;
}

/**
 * Builds a URL from a slug + optional query bag.
 */
function buildPath(leadId: string, route: StatusRoute): string {
  const base = `/dashboard/applications/new-application/status/${route.slug}/${leadId}`;
  if (!route.query || Object.keys(route.query).length === 0) return base;
  const params = new URLSearchParams(route.query);
  return `${base}?${params.toString()}`;
}

/**
 * Synchronous shallow path — used as a fallback (e.g. when workflow status fails).
 */
export function getStatusMarkPath(
  leadId: string,
  mark: NewApplicationListItemMark,
): string {
  return buildPath(leadId, resolveStatusRouteShallow(mark.status));
}

/**
 * Async deep resolution: fetches the lead's workflow progress and, for branches
 * that have sub-steps inside their view (currently FC Fail), appends `&substep=`
 * pointing at the last completed step. Falls back to the shallow path when the
 * workflow API fails or the branch has no completed sub-step yet.
 */
export async function resolveStatusMarkPath(
  leadId: string,
  mark: NewApplicationListItemMark,
): Promise<string> {
  const shallow = resolveStatusRouteShallow(mark.status);
  const status = (mark.status ?? "").trim().toLowerCase();
  const needsDeepResolution = status === "formalities check fail";
  if (!needsDeepResolution) return buildPath(leadId, shallow);

  try {
    // FC Fail substeps are recorded per-TM; scope progress to mark.tmNo so other
    // TMs that have advanced further on the same lead don't push the landing
    // substep to a later step than this TM has actually reached.
    const workflow = await getWorkflowStatus(leadId, mark.tmNo);
    const steps = (workflow.steps ?? []) as WorkflowStep[];
    const substep = pickFcFailSubstep(steps);
    if (!substep) return buildPath(leadId, shallow);
    return buildPath(leadId, {
      slug: shallow.slug,
      query: { ...(shallow.query ?? {}), substep },
    });
  } catch {
    return buildPath(leadId, shallow);
  }
}

/**
 * Chip label shown for a per-word entry in the Mark cell — e.g. "Word, 2".
 * Falls back to the TM number when type / class are missing.
 */
export function formatStatusMarkChipLabel(
  mark: NewApplicationListItemMark,
): string {
  const type = mark.typeOfMark?.trim() ?? "";
  const cls =
    typeof mark.classNumber === "number" && Number.isFinite(mark.classNumber)
      ? String(mark.classNumber)
      : "";
  if (type && cls) return `${type}, ${cls}`;
  if (type) return type;
  if (cls) return cls;
  return mark.tmNo?.trim() || "—";
}
